import { images } from '@/asset';
import { Enum } from '@/constants';
import {
  ConditionHidePayment,
  CriteriaHidePayment,
  CriteriaType,
  DayType,
  IsVpn,
  NameType,
  OrderRisk,
  SubKeyHelpCenter,
} from './enum';
import { TFunction } from 'i18next';
import { capitalize } from 'lodash';

export const perPageOptions = [
  {
    label: '10',
    value: '10',
  },
  {
    label: '25',
    value: '25',
  },
  {
    label: '50',
    value: '50',
  },
  {
    label: '100',
    value: '100',
  },
];

const typeList = [
  {
    label: 'Black list',
    value: Enum.ListType.BlackList,
  },
  {
    label: 'White list',
    value: Enum.ListType.WhiteList,
  },
];

export const orderTypeOptions = (t: TFunction) => [
  {
    label: t('fraud_orders:low_risk'),
    value: OrderRisk.Low,
  },
  {
    label: t('fraud_orders:medium_risk'),
    value: OrderRisk.Medium,
  },
  {
    label: t('fraud_orders:high_risk'),
    value: OrderRisk.High,
  },
  {
    label: t('common:all'),
    value: '',
  },
];

export const orderVpnOptions = (t: TFunction) => [
  {
    label: t('fraud_orders:yes'),
    value: IsVpn.Yes,
  },
  {
    label: t('fraud_orders:no'),
    value: IsVpn.No,
  },
  {
    label: t('fraud_orders:not_detected'),
    value: IsVpn.NotDetected,
  },
  {
    label: t('common:all'),
    value: '',
  },
];

const criteriaFilters = (t: TFunction) => [
  {
    label: t('common:ip_address'),
    value: Enum.CriteriaType.IpAddress,
  },
  {
    label: t('common:ip_address_start_with'),
    value: Enum.CriteriaType.IpAddressStartWith,
  },
  {
    label: capitalize(t('common:country')),
    value: Enum.CriteriaType.Country,
  },
  {
    label: t('common:state_province'),
    value: Enum.CriteriaType.Province,
  },
  {
    label: t('common:internet_service_provider'),
    value: Enum.CriteriaType.ISP,
  },
  {
    label: capitalize(t('common:collection')),
    value: Enum.CriteriaType.Collection,
    hiddenBlockAccess: true,
  },
  {
    label: capitalize(t('common:products')),
    value: Enum.CriteriaType.Product,
    hiddenBlockAccess: true,
  },
  {
    label: capitalize(t('common:specific_page')),
    value: Enum.CriteriaType.SpecificPage,
    hiddenBlockAccess: true,
  },
  {
    label: capitalize(t('common:referral_url')),
    value: Enum.CriteriaType.ReferralLink,
    hiddenBlockAccess: true,
  },
  {
    label: capitalize(t('common:user_agent')),
    value: Enum.CriteriaType.UserAgent,
  },
  {
    label: capitalize(t('common:ip_ranges')),
    value: Enum.CriteriaType.IpRanges,
  },
];
const criteriaFiltersBlockCheckout = [
  {
    label: 'IP address',
    value: Enum.CriteriaType.IpAddress,
  },
  {
    label: 'IP address start with',
    value: Enum.CriteriaType.IpAddressStartWith,
  },
  {
    label: 'Country',
    value: Enum.CriteriaType.Country,
  },
  {
    label: 'State/Province',
    value: Enum.CriteriaType.Province,
  },
  {
    label: 'Internet service provider (ISP)',
    value: Enum.CriteriaType.ISP,
  },
  {
    label: 'User Agent',
    value: Enum.CriteriaType.UserAgent,
  },
  {
    label: 'IP ranges',
    value: Enum.CriteriaType.IpRanges,
  },
  {
    label: 'VPN/Proxy',
    value: Enum.CriteriaType.VpnProxy,
  },
];

const blockTypeOptions = (t: TFunction) => [
  {
    label: t('common:block') || 'Block',
    value: Enum.ActionType.Block,
  },
  {
    label: t('common:redirect') || 'Redirect',
    value: Enum.ActionType.Redirect,
  },
];

const listTypeOptions = (t: TFunction) => [
  {
    content: capitalize(t('common:block')),
    id: Enum.ActionType.Block,
  },
  {
    content: capitalize(t('common:redirect')),
    id: Enum.ActionType.Redirect,
  },
  {
    content: capitalize(t('common:whitelist')),
    id: Enum.ActionType.WhiteList,
  },
  // {
  //   content: capitalize(t('common:hide_payment')),
  //   id: '3',
  // },
  // {
  //   content: capitalize(t('common:hide_shipping')),
  //   id: '4',
  // },
];

const configRules: (t: TFunction) => Array<{
  id: string;
  description: string;
  src: string;
  hiddenBlockAccess?: boolean;
  subKeyHelpCenter?: SubKeyHelpCenter;
}> = (t: TFunction) => [
  {
    subKeyHelpCenter: SubKeyHelpCenter.IpAddress,
    id: Enum.CriteriaType.IpAddress,
    description: t('common:ip_address'),
    src: images.block_ip_address,
  },
  {
    id: Enum.CriteriaType.IpAddressStartWith,
    description: t('common:ip_start_with'),
    src: images.block_ip_range,
    hiddenBlockAccess: true,
  },
  {
    subKeyHelpCenter: SubKeyHelpCenter.Location,
    id: Enum.CriteriaType.Country,
    description: t('common:location'),
    src: images.countries,
  },
  {
    id: Enum.CriteriaType.Province,
    description: t('common:location'),
    src: images.block_state,
    hiddenBlockAccess: true,
  },
  {
    id: Enum.CriteriaType.Collection,
    description: t('common:products'),
    src: images.block_collection,
    hiddenBlockAccess: true,
  },
  {
    subKeyHelpCenter: SubKeyHelpCenter.Product,
    id: Enum.CriteriaType.Product,
    description: t('common:products'),
    src: images.products,
    hiddenBlockAccess: true,
  },
  {
    subKeyHelpCenter: SubKeyHelpCenter.Agent,
    id: Enum.CriteriaType.UserAgent,
    description: t('common:user_agent'),
    src: images.userAgent,
  },
  {
    id: Enum.CriteriaType.IpRanges,
    description: t('common:ip_ranges'),
    src: images.block_ip_ranges,
    hiddenBlockAccess: true,
  },
  {
    subKeyHelpCenter: SubKeyHelpCenter.Page,

    id: Enum.CriteriaType.SpecificPage,
    description: t('common:specific_page'),
    src: images.block_specific_page,
    hiddenBlockAccess: true,
  },
  {
    subKeyHelpCenter: SubKeyHelpCenter.Isp,

    id: Enum.CriteriaType.ISP,
    description: t('common:isp'),
    src: images.block_isp,
  },
  {
    subKeyHelpCenter: SubKeyHelpCenter.Url,

    id: Enum.CriteriaType.ReferralLink,
    description: t('common:referrer_url'),
    src: images.referralLink,
    hiddenBlockAccess: true,
  },
  {
    subKeyHelpCenter: SubKeyHelpCenter.VpnProxy,
    id: Enum.CriteriaType.VpnProxy,
    description: t('common:vpn_proxy'),
    src: images.vpn,
  },
];

export const ipOptionsAddress = [
  {
    label: 'IP address',
    value: CriteriaType.IpAddress,
  },
  {
    label: 'IP start with',
    value: CriteriaType.IpAddressStartWith,
  },
  {
    label: 'IP ranges',
    value: CriteriaType.IpRanges,
  },
];
export const ipOptionsAddressFree = [
  {
    label: 'IP address',
    value: CriteriaType.IpAddress,
  },
  {
    label: 'IP start with',
    value: CriteriaType.IpAddressStartWith,
  },
];

export const ipOptionsLocation = [
  {
    label: 'IP Country',
    value: CriteriaType.Country,
  },
  {
    label: 'IP Province',
    value: CriteriaType.Province,
  },
];

export const optionsCollectionAndProduct = [
  {
    label: 'Collection',
    value: CriteriaType.Collection,
  },
  {
    label: 'Specific product',
    value: CriteriaType.Product,
  },
];
// HIDE PAYMENT
const criteriaHidePaymentFilter = [
  {
    label: 'Day of week',
    value: Enum.CriteriaHidePayment.DayOfWeek,
  },
  {
    label: 'New customer',
    value: Enum.CriteriaHidePayment.NewCustomer,
  },
  {
    label: 'Repeat customer',
    value: Enum.CriteriaHidePayment.RepeatCustomer,
  },
  // {
  //   label: 'Customer total spent',
  //   value: Enum.CriteriaHidePayment.CustomerTotalSpent,
  // },
  {
    label: 'Customer tags',
    value: Enum.CriteriaHidePayment.CustomerTags,
  },
  {
    label: 'Email',
    value: Enum.CriteriaHidePayment.Email,
  },
  {
    label: 'Email contain',
    value: Enum.CriteriaHidePayment.EmailDomain,
  },
  {
    label: 'Phone number',
    value: Enum.CriteriaHidePayment.Phone,
  },
  {
    label: 'Country',
    value: Enum.CriteriaHidePayment.Country,
  },
  // {
  //   label: 'State',
  //   value: Enum.CriteriaHidePayment.State,
  // },
  // {
  //   label: 'City',
  //   value: Enum.CriteriaHidePayment.City,
  // },
  {
    label: 'Zip code',
    value: Enum.CriteriaHidePayment.ZipCode,
  },
];

const configRulesPayment: Array<{
  id: string;
  label: string;
  title: string;
  src: string;
  condition: ConditionHidePayment;
  tooltip?: string;
}> = [
  {
    id: Enum.CriteriaHidePayment.DayOfWeek,
    label: 'Day of the week',
    title: 'Day of the week',
    src: images.hp_date_time,
    condition: ConditionHidePayment.DayOfTheWeek,
  },
  {
    id: Enum.CriteriaHidePayment.NewCustomer,
    label: 'Customer',
    title: 'New customer',
    src: images.hp_customer,
    condition: ConditionHidePayment.Customer,
  },
  {
    id: Enum.CriteriaHidePayment.RepeatCustomer,
    label: 'Customer',
    title: 'Repeat customer',
    src: images.hp_customer,
    condition: ConditionHidePayment.Customer,
  },
  {
    id: Enum.CriteriaHidePayment.CustomerTags,
    label: 'Customer',
    title: 'Customer tags',
    src: images.hp_customer,
    condition: ConditionHidePayment.Customer,
  },
  {
    id: Enum.CriteriaHidePayment.Email,
    label: 'Contact',
    title: 'Email',
    src: images.hp_contact,
    condition: ConditionHidePayment.Contact,
  },
  {
    id: Enum.CriteriaHidePayment.EmailDomain,
    label: 'Contact',
    title: 'Email contain',
    src: images.hp_contact,
    condition: ConditionHidePayment.Contact,
  },
  {
    id: Enum.CriteriaHidePayment.Phone,
    label: 'Contact',
    title: 'Phone',
    src: images.hp_contact,
    condition: ConditionHidePayment.Contact,
  },
  {
    id: Enum.CriteriaHidePayment.Country,
    label: 'Address',
    title: 'Country',
    src: images.hp_shipping_address,
    condition: ConditionHidePayment.Address,
  },
  {
    id: Enum.CriteriaHidePayment.ZipCode,
    label: 'Address',
    title: 'Zip code',
    src: images.hp_shipping_address,
    condition: ConditionHidePayment.Address,
  },
];

export const weekOption = [
  {
    label: 'Monday',
    value: DayType.monday,
  },
  {
    label: 'Tuesday',
    value: DayType.tuesday,
  },
  {
    label: 'Wednesday',
    value: DayType.wednesday,
  },
  {
    label: 'Thursday',
    value: DayType.thursday,
  },
  {
    label: 'Friday',
    value: DayType.friday,
  },
  {
    label: 'Saturday',
    value: DayType.saturday,
  },
  {
    label: 'Sunday',
    value: DayType.sunday,
  },
];

export const customer = [
  {
    label: 'New customer',
    value: CriteriaHidePayment.NewCustomer,
  },
  {
    label: 'Repeat customer',
    value: CriteriaHidePayment.RepeatCustomer,
  },
  {
    label: 'Customer tags',
    value: CriteriaHidePayment.CustomerTags,
  },
];

export const contact = [
  {
    label: 'Email',
    value: CriteriaHidePayment.Email,
  },
  {
    label: 'Email contain',
    value: CriteriaHidePayment.EmailDomain,
  },
  {
    label: 'Phone number',
    value: CriteriaHidePayment.Phone,
  },
];

export const nameType = (t: TFunction) => [
  {
    label: t('block_checkout:first_name'),
    value: NameType.FirstName,
  },
  {
    label: t('block_checkout:last_name'),
    value: NameType.LastName,
  },
  {
    label: t('block_checkout:full_name'),
    value: NameType.FullName,
  },
];

export const shippingAddress = [
  {
    label: 'Country',
    value: CriteriaHidePayment.Country,
  },
  // {
  //   label: "State",
  //   value: CriteriaHidePayment.State
  // },
  // {
  //   label: "City",
  //   value: CriteriaHidePayment.City
  // },
  {
    label: 'Zip code',
    value: CriteriaHidePayment.ZipCode,
  },
];

export const saleChannelOptions = [
  {
    label: 'Online Store',
    value: '580111',
  },
  {
    label: 'Point of Sale',
    value: '129785',
  },
  {
    label: 'Draft Orders',
    value: '1354745',
  },
  {
    label: 'Google & YouTube',
    value: '1780363',
  },
  {
    label: 'Facebook & Instagram',
    value: '2329312',
  },

  {
    label: 'Inbox',
    value: '3026047',
  },
  {
    label: 'Pinterest',
    value: '3009811',
  },
  {
    label: 'TikTok',
    value: '4383523',
  },
  {
    label: 'Shop',
    value: '3890849',
  },
  {
    label: 'Headless',
    value: '12875497473',
  },
  {
    label: 'Marketplace Connect',
    value: '1777077',
  },
];

export const orderStatusOptions = (t: TFunction) => [
  {
    label: t('fraud_orders:paid'),
    value: 'paid',
    tone: 'success',
  },
  {
    label: t('fraud_orders:refunded'),
    value: 'refunded',
    tone: 'info',
  },
  {
    label: t('fraud_orders:cancelled'),
    value: 'cancelled',
    tone: 'critical',
  },
  {
    label: t('fraud_orders:partially_paid'),
    value: 'partially_paid',
    tone: 'info',
  },
  {
    label: t('fraud_orders:pending'),
    value: 'pending',
    tone: 'warning',
  },
  {
    label: t('fraud_orders:authorized'),
    value: 'authorized',
    tone: 'success',
  },
  {
    label: t('fraud_orders:partially_refunded'),
    value: 'partially_refunded',
    tone: 'warning',
  },
  {
    label: t('fraud_orders:voided'),
    value: 'voided',
    tone: 'info',
  },
];

export const conditionToCriteriaMapping = {
  [ConditionHidePayment.DayOfTheWeek]: CriteriaHidePayment.DayOfWeek,
  [ConditionHidePayment.Customer]: CriteriaHidePayment.NewCustomer,
  [ConditionHidePayment.Contact]: CriteriaHidePayment.Email,
  [ConditionHidePayment.Address]: CriteriaHidePayment.Country,
};

export const criteriaToConditionMapping = {
  [CriteriaHidePayment.DayOfWeek]: ConditionHidePayment.DayOfTheWeek,
  [CriteriaHidePayment.NewCustomer]: ConditionHidePayment.Customer,
  [CriteriaHidePayment.RepeatCustomer]: ConditionHidePayment.Customer,
  [CriteriaHidePayment.CustomerTags]: ConditionHidePayment.Customer,
  [CriteriaHidePayment.Email]: ConditionHidePayment.Contact,
  [CriteriaHidePayment.EmailDomain]: ConditionHidePayment.Contact,
  [CriteriaHidePayment.Phone]: ConditionHidePayment.Contact,
  [CriteriaHidePayment.Country]: ConditionHidePayment.Address,
  [CriteriaHidePayment.CustomerTotalSpent]: ConditionHidePayment.Customer,
  [CriteriaHidePayment.ZipCode]: ConditionHidePayment.Address,
  [CriteriaHidePayment.State]: ConditionHidePayment.Address,
  [CriteriaHidePayment.City]: ConditionHidePayment.Address,
};

export default {
  configRules,
  criteriaFilters,
  blockTypeOptions,
  typeList,
  perPageOptions,
  listTypeOptions,
  ipOptionsAddress,
  ipOptionsAddressFree,
  ipOptionsLocation,
  optionsCollectionAndProduct,
  configRulesPayment,
  weekOption,
  customer,
  shippingAddress,
  criteriaHidePaymentFilter,
  conditionToCriteriaMapping,
  criteriaToConditionMapping,
  criteriaFiltersBlockCheckout,
  nameType,
};
